// AI-Era Cyber Defense Maturity Model — weighted-workstream Stage calculation.
//
// Implements the framework's own scoring method (framework/ai_era_cyber_defense_model.md
// §"Calculating Your AI-Era Cyber Defense Maturity Score", Steps 1–6) which the
// risk-exposure engine (scoring/index.ts) does NOT compute. Risk exposure and
// maturity Stage answer different questions — exposure = "how much residual
// risk", Stage = "how mature is the program on the 1–5 ladder" — so both are
// surfaced side by side.
//
// D3 (Elevate Your Security Posture) plays the governance-floor role: identity,
// segmentation, agent protection, and agentic SOC are the connective controls
// that gate how much the other two workstreams' automation can be trusted, so
// a weak D3 caps the overall stage the same way a weak governance pillar did
// in the upstream SANS-derived engine this file was adapted from.
//
// Pure and dependency-free so it is trivially unit-testable.

export type IndustryProfileId =
  | "default"
  | "financial_services"
  | "healthcare"
  | "technology"
  | "government"
  | "critical_infrastructure"
  | "education";

export type PillarWeights = { D1: number; D2: number; D3: number };

export type IndustryProfile = {
  id: IndustryProfileId;
  label: string;
};

// Industry vertical is captured for report context only — it no longer
// changes the Stage weighting (see PILLAR_WEIGHTS below). Previously each
// profile carried its own D1/D2/D3 weighting; removed rather than left
// dormant since nothing reads per-profile weights anymore.
export const INDUSTRY_PROFILES: Record<IndustryProfileId, IndustryProfile> = {
  default: { id: "default", label: "Default" },
  financial_services: { id: "financial_services", label: "Financial Services" },
  healthcare: { id: "healthcare", label: "Healthcare" },
  technology: { id: "technology", label: "Technology / AI-Native" },
  government: { id: "government", label: "Government / Defense" },
  critical_infrastructure: { id: "critical_infrastructure", label: "Critical Infrastructure" },
  education: { id: "education", label: "Education & Research" },
};

export const DEFAULT_INDUSTRY_PROFILE: IndustryProfileId = "default";

// Step 1 — the one uniform workstream weighting, used for every organization
// regardless of industry profile.
export const PILLAR_WEIGHTS: PillarWeights = { D1: 0.35, D2: 0.3, D3: 0.35 };

export type StageInfo = {
  stage: number; // integer 1..5
  label: string; // e.g. "Stage 3: Defined (Established)"
};

// Step 5 — raw-score → stage mapping (verbatim table).
export function rawToStage(raw: number): StageInfo {
  const r = Math.max(0, Math.min(5, raw));
  // V4.1 (FINDINGS.md F-13): boundaries are centred on each level, so answering
  // level N across the board reports stage N. The previous mapping was
  // inclusive-from-above (raw 1.0 -> stage 2, raw 3.0 -> stage 4), which told an
  // organization with nothing in place that it was "Reactive" and one answering
  // 3 everywhere that it was "Managed". It also made the cap rules inert: a
  // pillar mean of 1 already produced stage 2, so `minPillarStage + 1` never
  // bound (cappedBy was `none` in 50 of 50 corpus scenarios).
  if (r < 0.5) return { stage: 1, label: "Stage 1: Unaware" };
  if (r < 1.5) return { stage: 1, label: "Stage 1: Unaware (Established)" };
  if (r < 2.0) return { stage: 2, label: "Stage 2: Reactive (Emerging)" };
  if (r < 2.5) return { stage: 2, label: "Stage 2: Reactive (Established)" };
  if (r < 3.0) return { stage: 3, label: "Stage 3: Defined (Emerging)" };
  if (r < 3.5) return { stage: 3, label: "Stage 3: Defined (Established)" };
  if (r < 4.0) return { stage: 4, label: "Stage 4: Managed (Emerging)" };
  if (r < 4.5) return { stage: 4, label: "Stage 4: Managed (Established)" };
  return { stage: 5, label: "Stage 5: Optimizing" };
}

const STAGE_NAME: Record<number, string> = {
  1: "Stage 1: Unaware",
  2: "Stage 2: Reactive",
  3: "Stage 3: Defined",
  4: "Stage 4: Managed",
  5: "Stage 5: Optimizing",
};

// The maturity questions collect answers on the framework's 0..5 evidence scale
// directly — every question offers an "L0 — None / Not present" option through
// "L5 — Optimizing" (see content/explanations/maturity/*). So the pillar means
// are already on the framework's 0..5 scale and feed the weighted-score, cap,
// and band tables verbatim, with no rescale. (Historically the app collected
// 1..5 and a linear rescale bridged to 0..5; that option gap is closed, so the
// rescale was removed to avoid shifting genuine answers downward.)

export type MaturityStageResult = {
  industryProfile: IndustryProfileId;
  pillarScores: PillarWeights; // 0..5 mean maturity per pillar (evidence scale)
  evidenceScores: PillarWeights; // identical to pillarScores; kept for result shape stability
  pillarStages: { D1: number; D2: number; D3: number }; // integer stage per pillar
  raw: number; // Step 3 weighted raw score, 0..5
  rawStage: StageInfo; // Step 5 stage from raw
  cap: number; // Step 4 maximum permitted stage
  finalStage: number; // Step 6 = min(rawStage, cap)
  finalLabel: string;
  cappedBy: CapRule;
};

// V4.2 (FINDINGS.md F-15): when the governance floor and the minimum-pillar rule
// bind at the same value, the old code reported `governance_floor` on an
// arbitrary `<=` tie-break — hiding the fact that the org has a second, equally
// binding weakness, and pointing remediation at only one of them. Both are now
// reported, and where only one label can be shown we prefer the minimum pillar
// because it names the specific pillar to fix.
export type CapRule = "none" | "governance_floor" | "minimum_pillar" | "both";

export const CAP_RULE_LABEL: Record<CapRule, string> = {
  none: "",
  governance_floor: "governance floor",
  minimum_pillar: "weakest pillar",
  both: "governance floor and weakest pillar",
};

/**
 * Steps 2–6. `pillarScores` are the mean maturity (0..5, evidence scale)
 * of each pillar's controls — already computed as `domainScores.current` in
 * scoring/index.ts. They are used directly, no rescale.
 *
 * `profileId` is recorded on the result (report context — "assessed as a
 * Financial Services organization") but no longer changes the weighting:
 * every organization uses the same PILLAR_WEIGHTS.
 */
export function computeMaturityStage(
  pillarScores: PillarWeights,
  profileId: IndustryProfileId = DEFAULT_INDUSTRY_PROFILE,
): MaturityStageResult {
  const profile = INDUSTRY_PROFILES[profileId] ?? INDUSTRY_PROFILES.default;
  const w = PILLAR_WEIGHTS;

  // Answers are already on the 0..5 evidence scale — use them as-is.
  const clampEvidence = (n: number) => Math.max(0, Math.min(5, n));
  const evidenceScores: PillarWeights = {
    D1: clampEvidence(pillarScores.D1),
    D2: clampEvidence(pillarScores.D2),
    D3: clampEvidence(pillarScores.D3),
  };

  // Step 2 + 3 — weighted raw overall score (on the evidence scale).
  const raw =
    evidenceScores.D1 * w.D1 + evidenceScores.D2 * w.D2 + evidenceScores.D3 * w.D3;

  // Per-pillar integer stages (used by the cap rules).
  const pillarStages = {
    D1: rawToStage(evidenceScores.D1).stage,
    D2: rawToStage(evidenceScores.D2).stage,
    D3: rawToStage(evidenceScores.D3).stage,
  };

  // Step 4 — cap = min(Govern stage + 1, lowest pillar stage + 1).
  const govCap = pillarStages.D3 + 1;
  const minPillar = Math.min(pillarStages.D1, pillarStages.D2, pillarStages.D3);
  const minCap = minPillar + 1;
  const cap = Math.min(5, govCap, minCap);

  // Step 5 + 6.
  const rawStage = rawToStage(raw);
  const finalStage = Math.min(rawStage.stage, cap);

  let cappedBy: MaturityStageResult["cappedBy"] = "none";
  if (finalStage < rawStage.stage) {
    // F-15: a rule "bit" if its own cap is the binding one. Both can bind at once.
    const govBinds = govCap === cap;
    const pillarBinds = minCap === cap;
    cappedBy =
      govBinds && pillarBinds ? "both" : govBinds ? "governance_floor" : "minimum_pillar";
  }

  return {
    industryProfile: profile.id,
    pillarScores,
    evidenceScores,
    pillarStages,
    raw,
    rawStage,
    cap,
    finalStage,
    finalLabel: STAGE_NAME[finalStage] ?? rawStage.label,
    cappedBy,
  };
}
